// frontend/src/hooks/useDateTimePreferences.js
import { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import referenceService from '../services/referenceService';
import { usePreferences } from '../contexts/PreferenceContext';
import {
    formatDateTime as formatDateTimeWithTimeZone,
    formatTime as formatTimeWithTimeZone
} from '../utils/dateTimeFormatter';

export const useDateTimePreferences = () => {
    // Get preferences from the EAV-based PreferenceContext
    const { preferences: eavPreferences } = usePreferences();

    // State for storing the current locale
    const [currentLocale, setCurrentLocale] = useState('en-US');

    // Derive preferences from EAV context with fallback defaults
    const preferences = {
        dateFormat: eavPreferences?.userGlobal?.date_format || 'MM/DD/YYYY',
        timeFormat: eavPreferences?.userGlobal?.time_format || '12h',
        language: eavPreferences?.userGlobal?.language || 'en',
        distanceUnit: eavPreferences?.userGlobal?.distance_unit || 'km',
        theme: eavPreferences?.userGlobal?.theme || 'light',
        globalThreshold: eavPreferences?.userGlobal?.global_filter_threshold || 10
    };

    // Function to get locale from language code using database
    const getLocaleFromLanguage = useCallback(async (languageCode) => {
        try {
            const locale = await referenceService.getLocaleForLanguage(languageCode);
            return locale;
        } catch (error) {
            console.error('Failed to get locale for language', { error: error.message, languageCode });
            // Default to English if service fails
            return 'en-US';
        }
    }, []);

    // Update locale when language preference changes
    useEffect(() => {
        const updateLocale = async () => {
            const locale = await getLocaleFromLanguage(preferences.language);
            setCurrentLocale(locale);
        };
        
        updateLocale();
    }, [preferences.language, getLocaleFromLanguage]);

    // Note: No longer need event listener since preferences come from PreferenceContext
    // which automatically updates when preferences change

    // Format date according to user preferences with language support
    const formatDate = useCallback((date, customFormat = null) => {
        const { dateFormat } = preferences;
        const format_to_use = customFormat || dateFormat;

        if (!date) return '';

        try {
            let dateObj;
            if (typeof date === 'string') {
                // Handle different string formats
                if (date.includes('T')) {
                    // ISO string with time
                    dateObj = parseISO(date);
                } else if (date.includes('-')) {
                    // YYYY-MM-DD format
                    dateObj = parseISO(date + 'T00:00:00');
                } else {
                    // Try to parse as is
                    dateObj = new Date(date);
                }
            } else if (date instanceof Date) {
                dateObj = date;
            } else {
                console.error('Invalid date type:', typeof date, date);
                return 'Invalid Date';
            }

            // Check if date is valid
            if (isNaN(dateObj.getTime())) {
                console.error('Invalid date object:', date, dateObj);
                return 'Invalid Date';
            }

            // Format according to user preference using date-fns format
            // Map user preference formats to date-fns patterns
            if (format_to_use === 'DD/MM/YYYY') {
                return format(dateObj, 'dd/MM/yyyy');
            } else if (format_to_use === 'YYYY-MM-DD') {
                return format(dateObj, 'yyyy-MM-dd');
            } else {
                // Default MM/DD/YYYY format
                return format(dateObj, 'MM/dd/yyyy');
            }
        } catch (error) {
            console.error('Date formatting failed', { error: error.message, date, customFormat });
            return date.toString();
        }
    }, [preferences]);

    // Format date for API calls (always ISO format)
    const formatDateISO = useCallback((date) => {
        if (!date) return '';
        
        try {
            const dateObj = typeof date === 'string' ? parseISO(date) : date;
            return format(dateObj, 'yyyy-MM-dd');
        } catch (error) {
            console.error('ISO date formatting failed', { error: error.message, date });
            return date.toString();
        }
    }, []);

    // Format time according to user preferences
    const formatTime = useCallback((date, customFormat = null, timeZone = null) => {
        const { timeFormat } = preferences;
        const format_to_use = customFormat || timeFormat;
        
        if (!date) return '';

        if (timeZone) {
            return formatTimeWithTimeZone(date, format_to_use, timeZone);
        }
        
        try {
            const dateObj = typeof date === 'string' ? parseISO(date) : date;
            
            if (format_to_use === '24h') {
                return format(dateObj, 'HH:mm');
            } else {
                return format(dateObj, 'h:mm a');
            }
        } catch (error) {
            console.error('Time formatting failed', { error: error.message, date, customFormat });
            return date.toString();
        }
    }, [preferences]);

    // Format time string (HH:mm) to display format
    const formatTimeString = useCallback((timeString) => {
        const { timeFormat } = preferences;
        
        if (!timeString) return '';
        
        try {
            // Create a date object with the time
            const [hours, minutes] = timeString.split(':');
            const date = new Date();
            date.setHours(parseInt(hours, 10));
            date.setMinutes(parseInt(minutes, 10));
            
            if (timeFormat === '24h') {
                return format(date, 'HH:mm');
            } else {
                return format(date, 'h:mm a');
            }
        } catch (error) {
            console.error('Time string formatting failed', { error: error.message, timeString });
            return timeString;
        }
    }, [preferences]);

    // Format date and time together
    const formatDateTime = useCallback((date, customDateFormat = null, customTimeFormat = null, timeZone = null) => {
        const { dateFormat, timeFormat } = preferences;
        const date_format = customDateFormat || dateFormat;
        const time_format = customTimeFormat || timeFormat;

        if (!date) return '';

        if (timeZone) {
            return formatDateTimeWithTimeZone(date, date_format, time_format, timeZone);
        }

        try {
            const dateObj = typeof date === 'string' ? parseISO(date) : date;

            // Get date part according to user preference
            let datePart;
            if (date_format === 'DD/MM/YYYY') {
                datePart = format(dateObj, 'dd/MM/yyyy');
            } else if (date_format === 'YYYY-MM-DD') {
                datePart = format(dateObj, 'yyyy-MM-dd');
            } else {
                // Default MM/DD/YYYY format
                datePart = format(dateObj, 'MM/dd/yyyy');
            }

            // Get time part
            const timePart = time_format === '24h' ? format(dateObj, 'HH:mm') : format(dateObj, 'h:mm a');

            return `${datePart} ${timePart}`;
        } catch (error) {
            console.error('DateTime formatting failed', { error: error.message, date, customDateFormat, customTimeFormat });
            return date.toString();
        }
    }, [preferences]);

    return {
        // Preference values
        dateFormat: preferences.dateFormat,
        timeFormat: preferences.timeFormat,
        language: preferences.language,
        distanceUnit: preferences.distanceUnit,
        currentLocale,
        
        // Formatting functions
        formatDate,
        formatDateISO,
        formatTime,
        formatTimeString,
        formatDateTime
    };
};
